import React from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { RoleScope } from 'further-types/admin-user-role';
import { Typography, Grid } from '@mui/material';

import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import { FieldRow } from 'components/FormElements';
import api from 'lib/trpcClient';
import Spacing from 'components/Spacing';
import PermissionGroupBox from './components/PermissionGroupBox';
import { FormData } from './types';
import {
  eisPermissionGroups,
  eisNonGroupedPermissions,
} from './permission-groups-config';
import {
  lpPermissionGroups,
  lpNonGroupedPermissions,
} from './lp-permission-groups-config';
import { AdminUserRealm } from 'further-types/admin-user';
import { FirmSelector } from 'components/FirmSelector';
import { useGetRole } from 'hooks/ui/useGetRole';

const ViewRolePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isSuperAdmin } = useGetRole();

  const { data: role, isFetching } = api.adminUserRole.get.useQuery(
    { roleId: id },
    { enabled: !!id },
  );

  const realm = role?.realm;
  const allPermissionGroups =
    realm === AdminUserRealm.Eis
      ? eisPermissionGroups
      : realm === AdminUserRealm.Lp
      ? lpPermissionGroups
      : [];
  const permissionGroups = allPermissionGroups.filter(
    (group) => !group.autoEnabled,
  );
  const nonGroupedPermissions =
    realm === AdminUserRealm.Eis
      ? eisNonGroupedPermissions
      : realm === AdminUserRealm.Lp
      ? lpNonGroupedPermissions
      : { groupItems: [], groupName: '', groupDescription: '' };

  const form = useForm<FormData>({
    values: {
      name: role?.name ?? '',
      description: role?.description ?? '',
      permissions: [...new Set([...(role?.permissions ?? [])])],
      firmId: role?.firmId ?? role?.lpFirmId,
    },
  });

  const pageTitle = `View ${realm ?? ''} Role`;

  if (isFetching) {
    return <PageContainer heading={pageTitle} loading={true} />;
  }

  if (!realm) {
    return <div>No realm selected</div>;
  }

  return (
    <PageContainer heading={pageTitle}>
      <PageContent withSpacing>
        <Grid container spacing={2}>
          <Grid size={8}>
            <Spacing size="md">
              <FieldRow title="Role Name" centerTitle>
                {role.name}
              </FieldRow>
              <FieldRow title="Description" centerTitle>
                {role.description || '—'}
              </FieldRow>
              {role.scope === RoleScope.Firm && isSuperAdmin ? (
                <FieldRow title="Firm" centerTitle>
                  <FirmSelector
                    firmId={role.firmId}
                    error={false}
                    disabled={true}
                    onChange={() => {
                      // do nothing
                    }}
                    helperText={undefined}
                  />
                </FieldRow>
              ) : null}
              <FieldRow title="Scope" centerTitle>
                {role.scope === RoleScope.Firm
                  ? 'Firm-specific'
                  : 'System-wide'}
              </FieldRow>

              <Typography variant="h2">Permissions</Typography>

              {permissionGroups.map((group) => (
                <PermissionGroupBox
                  key={group.groupName}
                  title={group.groupName}
                  description={group.groupDescription}
                  items={group.groupItems}
                  allItems={[]}
                  form={form}
                  readOnly
                />
              ))}

              {nonGroupedPermissions.groupItems.length > 0 ? (
                <PermissionGroupBox
                  title={nonGroupedPermissions.groupName}
                  description={nonGroupedPermissions.groupDescription}
                  items={nonGroupedPermissions.groupItems}
                  allItems={[]}
                  form={form}
                  readOnly
                />
              ) : null}
            </Spacing>
          </Grid>
        </Grid>
      </PageContent>
    </PageContainer>
  );
};

export default ViewRolePage;
